"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import type { ColorPaletteId } from "@/lib/theme/color-palettes";
import { DEFAULT_PALETTE_ID } from "@/lib/theme/color-palettes";
import type { DashboardAppearance } from "@/lib/theme/dashboard-appearance";
import { DEFAULT_DASHBOARD_APPEARANCE } from "@/lib/theme/dashboard-appearance";
import { useTenantPersonalizationGet } from "@/features/profile-settings/hooks/useTenantPersonalizationGet";
import { buildPersonalizationSyncKey } from "@/lib/utils/personalization";
import {
  getThemeScope,
  isDashboardAppearanceRoute,
  type ThemeScope,
} from "@/lib/utils/theme-scope";
import { usePathname } from "next/navigation";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";

export type DashboardMode = "dark" | "light";
export type ThemeModeControl = "user-choice" | "light-only" | "dark-only";
export type FontSize = "small" | "medium" | "large";

interface DashboardThemeContextValue {
  mode: DashboardMode;
  setMode: (mode: DashboardMode) => void;
  toggleMode: () => void;
  colorPalette: ColorPaletteId;
  setColorPalette: (palette: ColorPaletteId) => void;
  previewPalette: ColorPaletteId | null;
  setPreviewPalette: (palette: ColorPaletteId | null) => void;
  customColor: string | null;
  setCustomColor: (color: string | null) => void;
  previewCustomColor: string | null;
  setPreviewCustomColor: (color: string | null) => void;
  grainEnabled: boolean;
  setGrainEnabled: (enabled: boolean) => void;
  previewGrainEnabled: boolean | null;
  setPreviewGrainEnabled: (enabled: boolean | null) => void;
  themeModeControl: ThemeModeControl;
  setThemeModeControl: (control: ThemeModeControl) => void;
  previewThemeModeControl: ThemeModeControl | null;
  setPreviewThemeModeControl: (control: ThemeModeControl | null) => void;
  gradientEnabled: boolean;
  setGradientEnabled: (enabled: boolean) => void;
  previewGradientEnabled: boolean | null;
  setPreviewGradientEnabled: (enabled: boolean | null) => void;
  fontSize: FontSize;
  setFontSize: (size: FontSize) => void;
  previewFontSize: FontSize | null;
  setPreviewFontSize: (size: FontSize | null) => void;
  canToggleTheme: boolean; // Computed: true if themeModeControl === "user-choice"
  dashboardAppearance: DashboardAppearance;
  setDashboardAppearance: (appearance: DashboardAppearance) => void;
  previewDashboardAppearance: DashboardAppearance | null;
  setPreviewDashboardAppearance: (appearance: DashboardAppearance | null) => void;
  /** Active appearance on dashboard routes (preview takes precedence). */
  effectiveDashboardAppearance: DashboardAppearance;
  /** Whether the current route is a signed-in portal dashboard. */
  themeScope: ThemeScope;
  /** Classic look on dashboard and admin auth routes when tenant appearance is classic. */
  isClassicDashboardActive: boolean;
}

export const DashboardThemeContext = createContext<
  DashboardThemeContextValue | undefined
>(undefined);

// Only mode (dark/light) is stored in localStorage as it's a user preference, not tenant personalization
const STORAGE_KEY = "bcm:dashboard:theme";

interface DashboardThemeProviderProps {
  children: ReactNode;
  initialPersonalization?: {
    colorPalette: ColorPaletteId;
    customColor: string | null;
    grainEnabled: boolean;
    themeModeControl?: ThemeModeControl;
    gradientEnabled?: boolean;
    fontSize?: FontSize;
    dashboardAppearance?: DashboardAppearance;
  } | null;
}

export const DashboardThemeProvider = ({
  children,
  initialPersonalization: propInitialPersonalization,
}: DashboardThemeProviderProps) => {
  // Use prop initialPersonalization if provided (from RootLayout server-side fetch)
  // This ensures no flash of default color - personalization is applied immediately
  const initialPersonalization = propInitialPersonalization;
  
  // Use lazy initialization to set initial state from initialPersonalization immediately
  // This prevents flash of default color on page load
  const [mode, setModeState] = useState<DashboardMode>(() => {
    // Mode is always from localStorage, but we can't access it during SSR
    // So we default to "dark" and update in useEffect
    return "dark";
  });
  const [colorPalette, setColorPaletteState] = useState<ColorPaletteId>(() => {
    return initialPersonalization?.colorPalette ?? DEFAULT_PALETTE_ID;
  });
  const [previewPalette, setPreviewPaletteState] = useState<ColorPaletteId | null>(null);
  const [customColor, setCustomColorState] = useState<string | null>(() => {
    return initialPersonalization?.customColor ?? null;
  });
  const [previewCustomColor, setPreviewCustomColorState] = useState<string | null>(null);
  const [grainEnabled, setGrainEnabledState] = useState<boolean>(() => {
    return initialPersonalization?.grainEnabled ?? true;
  });
  const [previewGrainEnabled, setPreviewGrainEnabledState] = useState<boolean | null>(null);
  const [themeModeControl, setThemeModeControlState] = useState<ThemeModeControl>(() => {
    return initialPersonalization?.themeModeControl ?? "user-choice";
  });
  const [previewThemeModeControl, setPreviewThemeModeControlState] = useState<ThemeModeControl | null>(null);
  const [gradientEnabled, setGradientEnabledState] = useState<boolean>(() => {
    return initialPersonalization?.gradientEnabled ?? true;
  });
  const [previewGradientEnabled, setPreviewGradientEnabledState] = useState<boolean | null>(null);
  const [fontSize, setFontSizeState] = useState<FontSize>(() => {
    return initialPersonalization?.fontSize ?? "medium";
  });
  const [previewFontSize, setPreviewFontSizeState] = useState<FontSize | null>(null);
  const [dashboardAppearance, setDashboardAppearanceState] =
    useState<DashboardAppearance>(() => {
      return (
        initialPersonalization?.dashboardAppearance ??
        DEFAULT_DASHBOARD_APPEARANCE
      );
    });
  const [previewDashboardAppearance, setPreviewDashboardAppearanceState] =
    useState<DashboardAppearance | null>(null);
  const [mounted, setMounted] = useState(false);
  const personalizationSyncKeyRef = useRef<string | null>(null);
  const pathname = usePathname();
  const themeScope = useMemo(() => getThemeScope(pathname), [pathname]);

  const tenantId = resolveEffectiveTenantNumber();

  // Fetch personalization sa API-ja (always enabled to keep in sync)
  // Note: GET endpoint doesn't require authentication, so it's safe to call
  const { data: personalizationData, isLoading: isLoadingPersonalization } =
    useTenantPersonalizationGet(
      { tenantId },
      {
        enabled: !!tenantId,
        initialData: propInitialPersonalization
          ? {
              colorPalette: propInitialPersonalization.colorPalette,
              customColor: propInitialPersonalization.customColor,
              grainEnabled: propInitialPersonalization.grainEnabled,
              gradientEnabled: propInitialPersonalization.gradientEnabled ?? true,
              themeModeControl:
                propInitialPersonalization.themeModeControl ?? "user-choice",
              fontSize: propInitialPersonalization.fontSize ?? "medium",
              dashboardAppearance:
                propInitialPersonalization.dashboardAppearance ??
                DEFAULT_DASHBOARD_APPEARANCE,
            }
          : undefined,
        // Public: trust SSR for 5 minutes, then refresh on focus.
        // Dashboard: paint from SSR, then refresh on mount.
        staleTime: themeScope === "dashboard" ? 0 : 5 * 60 * 1000,
        refetchOnMount: themeScope === "dashboard",
        refetchOnWindowFocus: themeScope === "public",
      },
    );

  // Initialize mode from localStorage (always, not part of personalization)
  // But respect themeModeControl - if it's light-only or dark-only, force that mode
  useEffect(() => {
    setMounted(true);
    // Proveri da li smo na client-u pre nego što pristupimo localStorage
    if (typeof window === "undefined") {
      return;
    }
    
    const effectiveThemeModeControl = previewThemeModeControl ?? themeModeControl;
    
    // If theme mode is locked, force that mode
    if (effectiveThemeModeControl === "light-only") {
      setModeState("light");
      localStorage.setItem(STORAGE_KEY, "light");
      return;
    }
    if (effectiveThemeModeControl === "dark-only") {
      setModeState("dark");
      localStorage.setItem(STORAGE_KEY, "dark");
      return;
    }
    
    // Otherwise, use stored preference
    const stored = localStorage.getItem(STORAGE_KEY) as DashboardMode | null;
    if (stored === "dark" || stored === "light") {
      setModeState(stored);
    } else {
      setModeState("dark");
    }
  }, [themeModeControl, previewThemeModeControl]);

  // Sync with API data when it loads (only if no previews)
  useEffect(() => {
    const hasActivePreview =
      previewPalette !== null ||
      previewCustomColor !== null ||
      previewGrainEnabled !== null ||
      previewThemeModeControl !== null ||
      previewGradientEnabled !== null ||
      previewFontSize !== null ||
      previewDashboardAppearance !== null;

    if (hasActivePreview) {
      return;
    }

    if (personalizationData && !isLoadingPersonalization) {
      const syncKey = buildPersonalizationSyncKey(personalizationData);
      if (syncKey === personalizationSyncKeyRef.current) {
        return;
      }
      personalizationSyncKeyRef.current = syncKey;

      if (personalizationData.colorPalette !== colorPalette) {
        setColorPaletteState(personalizationData.colorPalette);
      }
      const apiCustomColor = personalizationData.customColor ?? null;
      const currentCustomColor = customColor ?? null;
      if (apiCustomColor !== currentCustomColor) {
        setCustomColorState(apiCustomColor);
      }
      if (personalizationData.grainEnabled !== grainEnabled) {
        setGrainEnabledState(personalizationData.grainEnabled);
      }
      if (personalizationData.dashboardAppearance !== dashboardAppearance) {
        setDashboardAppearanceState(personalizationData.dashboardAppearance);
      }
    }
  }, [
    personalizationData,
    isLoadingPersonalization,
    colorPalette,
    customColor,
    grainEnabled,
    dashboardAppearance,
    previewPalette,
    previewCustomColor,
    previewGrainEnabled,
    previewThemeModeControl,
    previewGradientEnabled,
    previewFontSize,
    previewDashboardAppearance,
  ]);

  const setMode = useCallback((newMode: DashboardMode) => {
    // Use functional update to ensure we get the latest state
    setModeState((prevMode) => {
      // Only update if actually different (prevents unnecessary re-renders)
      if (prevMode === newMode) return prevMode;

      // Update localStorage immediately
      if (typeof window !== "undefined") {
        try {
          localStorage.setItem(STORAGE_KEY, newMode);
        } catch (e) {
          // Ignore localStorage errors
        }
      }

      return newMode;
    });
  }, []);

  const toggleMode = useCallback(() => {
    // Don't allow toggle if theme mode is locked
    const effectiveThemeModeControl = previewThemeModeControl ?? themeModeControl;
    if (effectiveThemeModeControl !== "user-choice") {
      return;
    }
    
    // Use flushSync to force immediate state update on mobile devices
    // This prevents the "stale state" issue where UI doesn't update until next interaction
    try {
      flushSync(() => {
        setModeState((prevMode) => {
          const newMode = prevMode === "dark" ? "light" : "dark";

          // Update localStorage synchronously
          if (typeof window !== "undefined") {
            try {
              localStorage.setItem(STORAGE_KEY, newMode);
            } catch (e) {
              // Ignore localStorage errors
            }
          }

          return newMode;
        });
      });
    } catch (e) {
      // Fallback if flushSync fails (shouldn't happen, but safety first)
      setModeState((prevMode) => {
        const newMode = prevMode === "dark" ? "light" : "dark";

        // Update localStorage synchronously
        if (typeof window !== "undefined") {
          try {
            localStorage.setItem(STORAGE_KEY, newMode);
          } catch (err) {
            // Ignore localStorage errors
          }
        }

        return newMode;
      });
    }
  }, [themeModeControl, previewThemeModeControl]);

  const setColorPalette = useCallback((newPalette: ColorPaletteId) => {
    setColorPaletteState((prevPalette) => {
      // Only update if actually different (prevents unnecessary re-renders)
      if (prevPalette === newPalette) return prevPalette;

      // Clear preview when saving
      setPreviewPaletteState(null);
      // If saving custom palette, also save the custom color
      if (newPalette === "custom" && previewCustomColor) {
        setCustomColorState(previewCustomColor);
        setPreviewCustomColorState(null);
      } else if (newPalette !== "custom") {
        // Don't clear custom color when switching away - user might come back
        // Only clear preview
        setPreviewCustomColorState(null);
      }

      return newPalette;
    });
  }, [previewCustomColor]);

  const setPreviewPalette = useCallback((newPalette: ColorPaletteId | null) => {
    setPreviewPaletteState(newPalette);
    // Clear preview custom color when switching away from custom
    if (newPalette !== "custom") {
      setPreviewCustomColorState(null);
    }
  }, []);

  const setPreviewCustomColor = useCallback((newColor: string | null) => {
    setPreviewCustomColorState(newColor);
  }, []);

  const setCustomColor = useCallback((newColor: string | null) => {
    setCustomColorState((prevColor) => {
      // Only update if actually different
      if (prevColor === newColor) return prevColor;
      return newColor;
    });
  }, []);

  const setGrainEnabled = useCallback((enabled: boolean) => {
    setGrainEnabledState((prevEnabled) => {
      // Only update if actually different
      if (prevEnabled === enabled) return prevEnabled;

      // Clear preview when saving
      setPreviewGrainEnabledState(null);

      return enabled;
    });
  }, []);

  const setPreviewGrainEnabled = useCallback((enabled: boolean | null) => {
    setPreviewGrainEnabledState(enabled);
  }, []);

  const setThemeModeControl = useCallback((control: ThemeModeControl) => {
    setThemeModeControlState((prevControl) => {
      if (prevControl === control) return prevControl;
      setPreviewThemeModeControlState(null);
      
      // If switching to light-only or dark-only, force that mode
      if (control === "light-only") {
        setModeState("light");
        if (typeof window !== "undefined") {
          localStorage.setItem(STORAGE_KEY, "light");
        }
      } else if (control === "dark-only") {
        setModeState("dark");
        if (typeof window !== "undefined") {
          localStorage.setItem(STORAGE_KEY, "dark");
        }
      }
      
      return control;
    });
  }, []);

  const setPreviewThemeModeControl = useCallback((control: ThemeModeControl | null) => {
    setPreviewThemeModeControlState(control);
    
    // If preview is set, apply it immediately
    if (control === "light-only") {
      setModeState("light");
      if (typeof window !== "undefined") {
        localStorage.setItem(STORAGE_KEY, "light");
      }
    } else if (control === "dark-only") {
      setModeState("dark");
      if (typeof window !== "undefined") {
        localStorage.setItem(STORAGE_KEY, "dark");
      }
    }
  }, []);

  const setGradientEnabled = useCallback((enabled: boolean) => {
    setGradientEnabledState((prevEnabled) => {
      if (prevEnabled === enabled) return prevEnabled;
      setPreviewGradientEnabledState(null);
      return enabled;
    });
  }, []);

  const setPreviewGradientEnabled = useCallback((enabled: boolean | null) => {
    setPreviewGradientEnabledState(enabled);
  }, []);

  const setFontSize = useCallback((size: FontSize) => {
    setFontSizeState((prevSize) => {
      if (prevSize === size) return prevSize;
      setPreviewFontSizeState(null);
      return size;
    });
  }, []);

  const setPreviewFontSize = useCallback((size: FontSize | null) => {
    setPreviewFontSizeState(size);
  }, []);

  const setDashboardAppearance = useCallback((appearance: DashboardAppearance) => {
    setDashboardAppearanceState((prev) => {
      if (prev === appearance) return prev;
      setPreviewDashboardAppearanceState(null);
      return appearance;
    });
  }, []);

  const setPreviewDashboardAppearance = useCallback(
    (appearance: DashboardAppearance | null) => {
      setPreviewDashboardAppearanceState(appearance);
    },
    [],
  );

  const effectiveDashboardAppearance = useMemo(() => {
    return previewDashboardAppearance ?? dashboardAppearance;
  }, [previewDashboardAppearance, dashboardAppearance]);

  const isClassicDashboardActive = useMemo(() => {
    return (
      isDashboardAppearanceRoute(pathname) &&
      effectiveDashboardAppearance === "classic"
    );
  }, [pathname, effectiveDashboardAppearance]);

  // Computed: can toggle theme only if user-choice
  const canToggleTheme = useMemo(() => {
    const effectiveThemeModeControl = previewThemeModeControl ?? themeModeControl;
    return effectiveThemeModeControl === "user-choice";
  }, [themeModeControl, previewThemeModeControl]);

  // Memoize context value to prevent unnecessary re-renders
  // But ensure it updates when mode or palette changes
  const contextValue = useMemo(
    () => ({ 
      mode, 
      setMode, 
      toggleMode, 
      colorPalette, 
      setColorPalette, 
      previewPalette, 
      setPreviewPalette,
      customColor,
      setCustomColor,
      previewCustomColor,
      setPreviewCustomColor,
      grainEnabled,
      setGrainEnabled,
      previewGrainEnabled,
      setPreviewGrainEnabled,
      themeModeControl,
      setThemeModeControl,
      previewThemeModeControl,
      setPreviewThemeModeControl,
      gradientEnabled,
      setGradientEnabled,
      previewGradientEnabled,
      setPreviewGradientEnabled,
      fontSize,
      setFontSize,
      previewFontSize,
      setPreviewFontSize,
      canToggleTheme,
      dashboardAppearance,
      setDashboardAppearance,
      previewDashboardAppearance,
      setPreviewDashboardAppearance,
      effectiveDashboardAppearance,
      themeScope,
      isClassicDashboardActive,
    }),
    [mode, setMode, toggleMode, colorPalette, setColorPalette, previewPalette, setPreviewPalette, customColor, setCustomColor, previewCustomColor, setPreviewCustomColor, grainEnabled, setGrainEnabled, previewGrainEnabled, setPreviewGrainEnabled, themeModeControl, setThemeModeControl, previewThemeModeControl, setPreviewThemeModeControl, gradientEnabled, setGradientEnabled, previewGradientEnabled, setPreviewGradientEnabled, fontSize, setFontSize, previewFontSize, setPreviewFontSize, canToggleTheme, dashboardAppearance, setDashboardAppearance, previewDashboardAppearance, setPreviewDashboardAppearance, effectiveDashboardAppearance, themeScope, isClassicDashboardActive],
  );

  // Always provide context, but use default 'dark' until mounted to prevent hydration mismatch
  // This ensures hooks always work, even during SSR/initial render
  return (
    <DashboardThemeContext.Provider value={contextValue}>
      {children}
    </DashboardThemeContext.Provider>
  );
};

export const useDashboardTheme = () => {
  const context = useContext(DashboardThemeContext);
  if (context === undefined) {
    throw new Error(
      "useDashboardTheme must be used within DashboardThemeProvider",
    );
  }
  return context;
};
